import React from 'react';
import { Check } from 'lucide-react';
import { AITool } from '../types';

interface PricingTableProps {
  tool: AITool;
}

export const PricingTable: React.FC<PricingTableProps> = ({ tool }) => {
  // Generate pricing tiers based on tool.pricingType
  const getPricingTiers = () => {
    switch (tool.pricingType) {
      case 'free':
        return [
          {
            name: 'Free',
            price: '$0',
            description: 'Basic features for everyone',
            features: [
              'Core functionality',
              'Basic support',
              'Limited usage'
            ],
            buttonText: 'Get Started',
            buttonLink: tool.tryNowLink,
            highlighted: true
          }
        ];
      case 'freemium':
        return [
          {
            name: 'Free',
            price: '$0',
            description: 'Basic features for everyone',
            features: [
              'Core functionality',
              'Community support',
              'Limited usage'
            ],
            buttonText: 'Get Started',
            buttonLink: tool.tryNowLink,
            highlighted: false
          },
          {
            name: 'Pro',
            price: '$19',
            period: '/month',
            description: 'Everything in Free, plus:',
            features: [
              'Advanced features',
              'Priority support',
              'Unlimited usage',
              'No watermarks'
            ],
            buttonText: 'Upgrade to Pro',
            buttonLink: tool.tryNowLink,
            highlighted: true
          }
        ];
      case 'paid':
        return [
          {
            name: 'Basic',
            price: '$9',
            period: '/month',
            description: 'Essential features for individuals',
            features: [
              'Core functionality',
              'Email support',
              'Limited usage'
            ],
            buttonText: 'Get Started',
            buttonLink: tool.tryNowLink,
            highlighted: false
          },
          {
            name: 'Pro',
            price: '$29',
            period: '/month',
            description: 'Advanced features for professionals',
            features: [
              'All Basic features',
              'Priority support',
              'Unlimited usage',
              'Advanced analytics'
            ],
            buttonText: 'Get Pro',
            buttonLink: tool.tryNowLink,
            highlighted: true
          },
          {
            name: 'Enterprise',
            price: 'Custom',
            description: 'Custom solutions for teams',
            features: [
              'All Pro features',
              'Dedicated support',
              'Custom integrations',
              'Team management'
            ],
            buttonText: 'Contact Sales',
            buttonLink: tool.website,
            highlighted: false
          }
        ];
      default:
        return [];
    }
  };

  const pricingTiers = getPricingTiers();

  if (pricingTiers.length === 0) {
    return null;
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
      <div className="p-6">
        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Pricing Plans</h3>
        
        <div className="space-y-4">
          {pricingTiers.map((tier, index) => (
            <div 
              key={index}
              className={`rounded-lg border ${
                tier.highlighted 
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' 
                  : 'border-gray-200 dark:border-gray-700'
              } p-4`}
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{tier.name}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{tier.description}</p>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{tier.price}</span>
                  {tier.period && (
                    <span className="text-sm text-gray-500 dark:text-gray-400">{tier.period}</span>
                  )}
                </div>
              </div>
              
              <ul className="space-y-2 mb-4">
                {tier.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start">
                    <Check size={16} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <a
                href={tier.buttonLink}
                target="_blank"
                rel="noopener noreferrer"
                className={`block w-full text-center py-2 rounded-md text-sm font-medium ${
                  tier.highlighted
                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                    : 'bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600'
                } transition-colors`}
              >
                {tier.buttonText}
              </a>
            </div>
          ))}
        </div>
        
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 text-center">
          Pricing information is estimated and may vary. Visit the official website for accurate details.
        </p>
      </div>
    </div>
  );
};